// Code here generates React content for both: Admin Editor and Help Center UI
// This is the reason why it is lightweight and doesn't use React or Slate libraries
//
// As well the structure of generated content is important and used by
// packages/tool/box/slate/extractBlockFromAtlasElement.tsx
// Ensure to update that file if you change the structure of generated content

function isObject(o) {
  return Object.prototype.toString.call(o) === "[object Object]";
}

function createBlockIdFromText(text) {
  return text
    .toLowerCase()
    .replace(/^[^a-z\d]+|[^a-z\d]+$/g, "")
    .replace(/[^a-z\d]+/g, "-");
}

function generateBlockId(node, idsMap) {
  if (!node.type.startsWith("heading-")) return undefined;
  const content = node.children.reduce((content, leaf) => content + leaf.text, "");
  const generated = createBlockIdFromText(content);
  idsMap[generated] = (idsMap[generated] || 0) + 1;
  return idsMap[generated] === 1 ? generated : generated + "_" + idsMap[generated];
}

function isPlainObject(o) {
  let ctor, prot;

  if (isObject(o) === false) return false;

  // If has modified constructor
  ctor = o.constructor;
  if (ctor === undefined) return true;

  // If has modified prototype
  prot = ctor.prototype;
  if (isObject(prot) === false) return false;

  // If constructor does not have an Object-specific method
  if (prot.hasOwnProperty("isPrototypeOf") === false) {
    return false;
  }

  // Most likely a plain Object
  return true;
}

// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
const renderInlineChromiumBugfix = (createElement, key) =>
  createElement(
    "span",
    { key, contentEditable: false, style: { fontSize: 0, lineHeight: 0 } },
    String.fromCodePoint(160) /* Non-breaking space */
  );

function isLeaf(value) {
  return isPlainObject(value) && "text" in value && typeof value.text === "string";
}

const renderBlockquote = (createElement, { attributes, children }) => {
  return createElement(
    "blockquote",
    Object.assign({}, attributes, {
      className: "atlas-block atlas-block-quote" + (attributes.className ? " " + attributes.className : ""),
    }),
    children
  );
};

const renderIcon = (createElement, { attributes, children, element }) => {
  return createElement(
    "figure",
    Object.assign({}, attributes, {
      className: "atlas-block atlas-block-icon" + (attributes.className ? " " + attributes.className : ""),
      contentEditable: false,
    }),
    [
      createElement(
        "div",
        {
          key: "icon-text",
          className: "atlas-block-icon-text",
        },
        children
      ),
      createElement("span", { key: "icon-emoji", className: "atlas-block-icon-emoji" }, element.icon),
    ]
  );
};

const renderCallout = (createElement, { attributes, children }) => {
  return createElement(
    "div",
    Object.assign({}, attributes, {
      className: "atlas-block atlas-block-callout" + (attributes.className ? " " + attributes.className : ""),
    }),
    children
  );
};

const renderHorizontalLineElement = (createElement, { attributes, children }) => {
  return createElement(
    "div",
    Object.assign({}, attributes, {
      className: "atlas-block atlas-block-horizontal-line" + (attributes.className ? " " + attributes.className : ""),
    }),
    children
  );
};

const renderImageElement = (createElement, { attributes, children, element, editing, extra }) => {
  const expand = !element.align || element.align === "justify";
  const center = element.align === "center" ? "center" : null;
  const float = !expand && !center && element.align !== "standard" ? element.align : null;

  return createElement(
    "div",
    Object.assign({}, attributes, {
      className:
        "atlas-block atlas-block-image" +
        (attributes.className ? " " + attributes.className : "") +
        (element.fingerprint ? " atlas-block-image__placeholder" : "") +
        (float ? " atlas-block-image__float-" + float : "") +
        (center ? " atlas-block-image__centered" : "") +
        (expand ? " atlas-block-image__expanded" : "") +
        (element.width ? " atlas-block-image__sized" : ""),
    }),
    createElement(
      !editing && element.link ? "a" : "div",
      Object.assign(
        {
          className: "atlas-block-image-container",
          contentEditable: false,
          ...(!editing && element.link && { href: element.link }),
        },
        element.width ? { style: { maxWidth: element.width + "px" } } : {}
      ),
      [
        ...(editing
          ? [
              createElement(
                "div",
                {
                  key: "image-text",
                  className: "atlas-block-image-text",
                },
                children
              ),
            ]
          : []),
        createElement("img", {
          key: "image",
          src: element.src || "/public/image-placeholder.svg",
          alt: element.alt,
          width: element.width,
        }),
        ...(editing && !element.fingerprint
          ? [
              createElement("span", {
                key: "image-resize-left",
                className: "atlas-block-image__resize-left",
              }),
              createElement("span", {
                key: "image-resize-right",
                className: "atlas-block-image__resize-right",
              }),
              createElement("span", {
                key: "image-resize-bottom",
                className: "atlas-block-image__resize-bottom",
              }),
            ]
          : []),
        extra,
      ]
    )
  );
};

const renderVideoElement = (createElement, { attributes, children, element }) => {
  const expand = !element.align || element.align === "justify";
  const center = element.align === "center" ? "center" : null;
  const float = !expand && !center && element.align !== "standard" ? element.align : null;

  return createElement(
    "div",
    Object.assign({}, attributes, {
      className:
        "atlas-block atlas-block-video" +
        (attributes.className ? " " + attributes.className : "") +
        (float ? " atlas-block-video__float-" + float : "") +
        (center ? " atlas-block-video__centered" : "") +
        (expand ? " atlas-block-video__expanded" : ""),
    }),
    createElement(
      "div",
      {
        className: "atlas-block-video-container",
        contentEditable: false,
      },
      [
        createElement(
          "div",
          {
            key: "video-text",
            className: "atlas-block-video-text",
          },
          children
        ),
        createElement(
          "video",
          {
            key: "video",
            controls: true,
            title: element.alt,
            width: typeof element.width === "number" ? element.width : 640,
            height: typeof element.height === "number" ? element.height : 320,
          },
          element.sources.map((source) =>
            createElement("source", {
              key: source.type,
              src: source.url,
              type: source.type,
            })
          )
        ),
      ]
    )
  );
};

const renderVideoEmbedElement = (createElement, { attributes, children, element }) => {
  const expand = !element.align || element.align === "justify";
  const center = element.align === "center" ? "center" : null;
  const float = !expand && !center && element.align !== "standard" ? element.align : null;

  const containerProps = Object.assign({}, attributes, {
    className:
      "atlas-block atlas-block-video-embed atlas-block-video-embed__" +
      element.service +
      (attributes.className ? " " + attributes.className : "") +
      (float ? " atlas-block-video-embed__float-" + float : "") +
      (center ? " atlas-block-video-embed__centered" : "") +
      (expand ? " atlas-block-video-embed__expanded" : ""),
    contentEditable: false,
  });

  return createElement("div", containerProps, [
    createElement(
      "div",
      {
        key: "leaf",
        className: "atlas-block-video-embed-text",
      },
      children
    ),
    element.service === "vimeo" &&
      createElement("iframe", {
        key: "video-vimeo",
        className: "atlas-block-video-embed-iframe",
        src: "https://player.vimeo.com/video/" + element.resourceId,
        title: element.alt,
        allowFullScreen: true,
        allow: "autoplay; fullscreen; picture-in-picture",
        width: 640,
        height: 360,
      }),
    element.service === "youtube" &&
      createElement("iframe", {
        key: "video-youtube",
        className: "atlas-block-video-embed-iframe",
        src: "https://www.youtube.com/embed/" + element.resourceId,
        title: element.alt,
        allowFullScreen: true,
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        width: 560,
        height: 315,
      }),
    element.service === "loom" &&
      createElement("iframe", {
        key: "video-loom",
        className: "atlas-block-video-embed-iframe",
        src: "https://www.loom.com/embed/" + element.resourceId,
        title: element.alt,
        allowFullScreen: true,
        width: 640,
        height: 400,
      }),
    element.service === "clueso" &&
      createElement("iframe", {
        key: "video-clueso",
        className: "atlas-block-video-embed-iframe",
        src: `https://${element.cluesoSubdomain}.clueso.io/embed/${element.resourceId}`,
        title: element.alt,
        allowFullScreen: true,
        width: 560,
        height: 315,
      }),
  ]);
};

const renderEmbedElement = (createElement, { attributes, children, element }) => {
  const isHtml = element.source.startsWith("<");
  const containerProps = Object.assign({}, attributes, {
    className: "atlas-block atlas-block-embed" + (attributes.className ? " " + attributes.className : ""),
    contentEditable: false,
    style: isHtml ? undefined : { height: element.height + "px" },
  });

  return createElement("div", containerProps, [
    createElement(
      "div",
      {
        key: "leaf",
        className: "atlas-block-embed-text",
      },
      children
    ),
    isHtml
      ? createElement("div", {
          className: "atlas-block-embed-html",
          dangerouslySetInnerHTML: { __html: element.source },
          "data-source": element.source,
        })
      : createElement("iframe", {
          className: "atlas-block-embed-iframe",
          key: "embed",
          title: element.alt,
          allowFullScreen: true,
          allow: "autoplay; fullscreen; picture-in-picture",
          src: element.source,
          "data-height": element.height,
        }),
    isHtml
      ? null
      : createElement("span", {
          key: "embed-resize",
          className: "atlas-block-embed__resize",
        }),
  ]);
};

const renderListElement = (createElement, { attributes, children, element }) => {
  const ordered = element.type === "numbered-list";

  children = children && Array.isArray(children) ? children : [];

  const elements = [];
  const listItems = [];
  let listItemNumber = 0;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];

    const isChildListItem =
      child.type === "li" ||
      (child.props.children &&
        child.props.children.props &&
        child.props.children.props.element &&
        child.props.children.props.element.type === "list-item");

    if (isChildListItem) {
      ++listItemNumber;
      listItems.push(child);
    }

    if ((!isChildListItem || i === children.length - 1) && listItems.length) {
      const start = !elements.length && "start" in element ? element.start || 1 : listItemNumber - listItems.length + 1;
      const listElementName = ordered ? "ol" : "ul";

      elements.push(
        createElement(
          listElementName,
          {
            key: listItemNumber,
            start,
            className: `atlas-block-list-${listElementName}-element`,
          },
          listItems.splice(0)
        )
      );
    }

    if (!isChildListItem) elements.push(child);
  }

  return createElement(
    "div",
    Object.assign({}, attributes, {
      className:
        "atlas-block atlas-block-list" +
        " atlas-block-list-" +
        (ordered ? "ordered" : "unordered") +
        (attributes.className ? " " + attributes.className : ""),
    }),
    elements
  );
};

const renderTableElement = (createElement, { attributes, children, element }) => {
  const expand = element.align === "justify";
  const center = element.align === "center" ? "center" : null;
  const float = !expand && !center && element.align !== "standard" ? element.align : null;

  return createElement(
    "div",
    {
      className:
        "atlas-block atlas-block-table" +
        (attributes.className ? " " + attributes.className : "") +
        (float ? " atlas-block-table__float-" + float : "") +
        (center ? " atlas-block-table__centered" : "") +
        (expand ? " atlas-block-table__expanded" : ""),
    },
    createElement(
      "table",
      attributes,
      element.columns
        ? [
            createElement(
              "colgroup",
              { key: "columns" },
              element.columns.map((column, index) =>
                createElement("col", {
                  key: index,
                  width: column.width || "auto",
                })
              )
            ),
            createElement("tbody", { key: "rows" }, children),
          ]
        : createElement("tbody", {}, children)
    )
  );
};

const renderTableCellElement = (createElement, { attributes, children, element }, Fragment) => {
  const align = element.align || null;

  return createElement(
    "td",
    Object.assign({}, attributes, {
      className:
        "atlas-block atlas-block-table-cell" +
        (attributes.className ? " " + attributes.className : "") +
        (align ? " atlas-block-table-cell__align-" + align : ""),
    }),
    createElement(Fragment, {}, [
      children,
      createElement("span", {
        key: "cell-resize",
        className: "atlas-block-table-cell__resize",
      }),
    ])
  );
};

function renderBlockElement(createElement, Fragment, props, options = {}) {
  const element = props.element;
  const attributes = props.attributes;
  const children = props.children;

  switch (element.type) {
    case "block-quote":
      return renderBlockquote(createElement, { attributes, children });
    case "heading-one":
      return createElement(
        "h1",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-heading-one" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "heading-two":
      return createElement(
        "h2",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-heading-two" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "heading-three":
      return createElement(
        "h3",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-heading-three" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "code-block":
      return createElement(
        "pre",
        Object.assign({}, attributes, {
          className:
            "atlas-block atlas-block-code language-" +
            (attributes.language || "clike") +
            (attributes.className ? " " + attributes.className : ""),
        }),
        createElement("code", {}, children)
      );
    case "numbered-list":
    case "bulleted-list":
      return renderListElement(createElement, {
        attributes,
        children,
        element,
      });
    case "list-item":
      return createElement(
        "li",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-list-item" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "table":
      return renderTableElement(createElement, {
        attributes,
        children,
        element,
      });
    case "table-row":
      return createElement(
        "tr",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-table-row" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "table-cell":
      return renderTableCellElement(
        createElement,
        {
          attributes,
          children,
          element,
        },
        Fragment
      );
    case "paragraph":
      return createElement(
        "p",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-paragraph" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
    case "horizontal-line":
      return renderHorizontalLineElement(createElement, {
        attributes,
        children,
      });
    case "image":
      return renderImageElement(createElement, {
        attributes,
        children,
        element,
        editing: options.editing,
        extra: options.extra,
      });
    case "video":
      return renderVideoElement(createElement, {
        attributes,
        children,
        element,
      });
    case "video-embed":
      return renderVideoEmbedElement(createElement, {
        attributes,
        children,
        element,
      });
    case "embed":
      return renderEmbedElement(createElement, {
        attributes,
        children,
        element,
      });
    case "callout":
      return renderCallout(createElement, {
        attributes,
        children,
      });
    case "icon":
      return renderIcon(createElement, {
        attributes,
        children,
        element,
      });
    default:
      return createElement(
        "div",
        Object.assign({}, attributes, {
          className: "atlas-block atlas-block-unknown" + (attributes.className ? " " + attributes.className : ""),
        }),
        children
      );
  }
}

function renderLeafElement(createElement, Fragment, props, options = {}) {
  const leaf = props.leaf;
  const attributes = props.attributes;

  let children = props.children;

  if (leaf.code) {
    children = createElement("code", { className: "atlas-leaf" }, children);
  }

  if (leaf.bold) {
    children = createElement("strong", { className: "atlas-leaf" }, children);
  }

  if (leaf.italic) {
    children = createElement("em", { className: "atlas-leaf" }, children);
  }

  if (leaf.underline) {
    children = createElement("u", { className: "atlas-leaf" }, children);
  }

  if (leaf.strikethrough) {
    children = createElement("s", { className: "atlas-leaf" }, children);
  }

  if (typeof leaf.link === "string") {
    children = createElement(
      "a",
      {
        href: leaf.link,
        className: "atlas-leaf",
        target: options && options.baseUrl && leaf.link.indexOf(options.baseUrl) === 0 ? "_self" : "_blank",
      },
      [
        renderInlineChromiumBugfix(createElement, "before"),
        createElement(Fragment, { key: "content" }, children),
        renderInlineChromiumBugfix(createElement, "after"),
      ]
    );
  }

  return createElement(
    "span",
    Object.assign(
      {},
      attributes,
      {
        className: "atlas-leaf" + (attributes.className ? " " + attributes.className : ""),
      },
      typeof leaf.constant === "string"
        ? {
            ["data-constant"]: leaf.constant,
          }
        : {}
    ),
    children
  );
}

function serialize(createElement, Fragment, node, idsMap, key = undefined, options = {}) {
  if (isLeaf(node)) {
    return renderLeafElement(
      createElement,
      Fragment,
      {
        attributes: { key },
        leaf: node,
        children: node.text,
      },
      options
    );
  } else {
    const id = generateBlockId(node, idsMap);
    const attributes = { key, id };
    return renderBlockElement(createElement, Fragment, {
      attributes,
      element: node,
      children: node.children.map((child, index) => serialize(createElement, Fragment, child, idsMap, index, options)),
      options,
    });
  }
}

/**
 *
 * @param {*} createElement
 * @param {*} Fragment
 * @param {*} nodes
 * @param {{ editing?: boolean; baseUrl?: string }} options
 * @returns
 */
function renderer(createElement, Fragment, nodes, options) {
  const idsMap = {};
  return createElement(
    Fragment,
    {},
    nodes.map((node, index) => serialize(createElement, Fragment, node, idsMap, index, options))
  );
}

export { isLeaf, renderBlockElement, renderLeafElement, renderer, generateBlockId };
